import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { AlertCircle } from 'lucide-react'
import {
  getLibraryLessonComponents,
  getTodayLesson,
  type ComponentCompleteResult,
  type LcComponentStatus,
  type LibraryLessonDetailResponse,
  type MaterialNotice,
  type TodayLessonResponse,
} from '@/api/lcLearning'
import ComponentRunner from '@/components/learner/ComponentRunner'
import { LearningSidebar } from '@/components/learner/ui/LearningSidebar'
import { CompletionCelebration } from '@/components/learner/CompletionCelebration'
import { ExitConfirmDialog } from '@/components/learner/ExitConfirmDialog'
import { useLearningTop } from '@/components/layout/LearningLayout'
import { Button3D } from '@/components/learner/ui/Button3D'
import { Card } from '@/components/learner/ui/Card'
import { LearnerLoading } from '@/components/learner/ui/LearnerLoading'
import { getLearnerErrorMessage } from '@/utils/errorMessage'
import { useSidebarCollapse } from '@/hooks/learner/useSidebarCollapse'
import { learnerLibraryQueryKey } from '@/hooks/learner/useLearnerLibrary'
import { specialLecturePath } from '@/hooks/learner/useSpecialLectureFlow'
import { findInitialActiveIndex, toLearnSteps } from './model/learnModel'
import { pickComponentLabel, type ComponentLabelFallbacks } from '@/utils/componentLabel'
import type { LearnStep } from './ui-model/types'

type Mode = 'progress' | 'review'

function isInSessionWrongReviewComponent(component: LcComponentStatus): boolean {
  if (component.type.toLowerCase() !== 'quiz') return false
  const extraction = (
    component.options as { extraction?: { answerFilter?: unknown } } | null
  )?.extraction
  return extraction?.answerFilter === 'wrong_in_review'
}

interface LoadedLesson {
  enrollmentId: number
  lessonId: number
  lessonTitle: string
  isFirstAttempt: boolean
  isDesignatedReview: boolean
  sorted: LcComponentStatus[]
  steps: LearnStep[]
  bookOpenNotice: MaterialNotice | null
}

export function fromToday(
  resp: TodayLessonResponse,
  lang: string,
  fallbacks?: ComponentLabelFallbacks,
): LoadedLesson {
  const { sorted, steps } = toLearnSteps(resp.components, lang, fallbacks)
  return {
    enrollmentId: resp.enrollmentId,
    lessonId: resp.lessonId,
    lessonTitle: resp.lessonTitle,
    isFirstAttempt: resp.isFirstAttempt,
    isDesignatedReview: resp.isDesignatedReview ?? false,
    sorted,
    steps,
    // 구버전 백엔드는 선택된 한 권을 materialNotices[0]에 내려준다.
    bookOpenNotice: resp.bookOpenNotice ?? resp.materialNotices?.[0] ?? null,
  }
}

export function fromLibraryDetail(
  resp: LibraryLessonDetailResponse,
  enrollmentIdFallback: number,
  lang: string,
  fallbacks?: ComponentLabelFallbacks,
): LoadedLesson {
  const revisitableComponents = resp.components.filter(
    (component) => !isInSessionWrongReviewComponent(component),
  )
  const { sorted, steps } = toLearnSteps(revisitableComponents, lang, fallbacks)
  return {
    enrollmentId: enrollmentIdFallback,
    lessonId: resp.lessonId,
    lessonTitle: resp.lessonTitle,
    isFirstAttempt: false,
    isDesignatedReview: false,
    sorted,
    steps,
    // 도서관 복습 경로에는 교재 펼침 안내 없음.
    bookOpenNotice: null,
  }
}

export default function LearnPage() {
  const { t, i18n } = useTranslation(['learner', 'common'])
  const { lessonId: lessonIdParam } = useParams<{ lessonId: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const mode: Mode = searchParams.get('mode') === 'review' ? 'review' : 'progress'
  const stepIdHint = searchParams.get('stepId') ?? undefined

  const [loaded, setLoaded] = useState<LoadedLesson | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeIndex, setActiveIndex] = useState<number>(-1)
  const [showExit, setShowExit] = useState(false)
  const [finished, setFinished] = useState(false)
  const [sidebarOpen, setSidebarOpen] = useState(false)

  const activeComp = loaded ? loaded.sorted[activeIndex] : undefined
  const {
    collapsed: sidebarCollapsed,
    autoCollapsed: sidebarAutoCollapsed,
    toggle: toggleSidebar,
    collapse: collapseSidebar,
    expand: expandSidebar,
  } = useSidebarCollapse({})

  const { setLessonLabel, setStepLabel, setProgressDots, setOnExit, setOnMenu } = useLearningTop()
  const finishedRef = useRef(false)
  const initializedSelectionKeyRef = useRef<string | null>(null)

  // 컴포넌트 semantic 라벨 fallback(명시 label 없을 때만). 예: 반짝깨처(직전 레슨 flash 영상).
  const labelFallbacks = useMemo<ComponentLabelFallbacks>(
    () => ({ flashQueture: t('learn.componentName.flashQueture') }),
    [t],
  )

  const load = useCallback(async () => {
    if (!lessonIdParam) {
      setError(t('learn.invalidPath'))
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    try {
      if (mode === 'review') {
        const [detail, today] = await Promise.all([
          getLibraryLessonComponents(Number(lessonIdParam)),
          getTodayLesson().catch(() => null),
        ])
        if (today?.specialLecture) {
          navigate(specialLecturePath(today.specialLecture), { replace: true })
          return
        }
        const enrollmentId = today?.enrollmentId ?? 0
        setLoaded(fromLibraryDetail(detail, enrollmentId, i18n.language, labelFallbacks))
      } else {
        const today = await getTodayLesson()
        if (today.specialLecture) {
          navigate(specialLecturePath(today.specialLecture), { replace: true })
          return
        }
        if (today.offlineTest) {
          navigate('/my/today', { replace: true })
          return
        }
        setLoaded(fromToday(today, i18n.language, labelFallbacks))
      }
    } catch (e) {
      console.error('LearnPage load failed', e)
      setError(getLearnerErrorMessage(e, t))
    } finally {
      setLoading(false)
    }
  }, [lessonIdParam, mode, t, i18n.language, labelFallbacks, navigate])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    if (!loaded) return
    const selectionKey = `${mode}:${loaded.enrollmentId}:${loaded.lessonId}:${stepIdHint ?? ''}`
    if (initializedSelectionKeyRef.current === selectionKey) return
    initializedSelectionKeyRef.current = selectionKey

    // 복습 회차도 일반 재개 경로 사용: 백엔드가 회차별 컴포넌트 status 를 정확히 반환하므로
    // 최초 진입 시에만 hint/첫 미완료 스텝을 적용하고, 이후 로컬 완료 갱신은 현재 선택을 유지한다.
    const idx = findInitialActiveIndex(loaded.sorted, stepIdHint)
    if (idx < 0) {
      setFinished(true)
      finishedRef.current = true
    } else {
      setActiveIndex(idx)
      setFinished(false)
      finishedRef.current = false
    }
  }, [loaded, mode, stepIdHint])

  useEffect(() => {
    if (!loaded) return
    setLessonLabel(loaded.lessonTitle)
    const active = loaded.sorted[activeIndex]
    setStepLabel(
      finished
        ? t('learn.completedTop')
        : active
          ? pickComponentLabel(active, i18n.language, labelFallbacks)
          : '',
    )
    const doneIndices = loaded.steps
      .map((s, i) => (s.status === 'done' ? i : -1))
      .filter((i) => i >= 0)
    setProgressDots({
      total: loaded.steps.length,
      currentIndex: finished ? -1 : activeIndex,
      doneIndices,
    })
  }, [loaded, activeIndex, finished, setLessonLabel, setStepLabel, setProgressDots, t, i18n.language, labelFallbacks])

  useEffect(() => {
    setOnExit(() => {
      if (finishedRef.current) {
        navigate('/my/today')
      } else {
        setShowExit(true)
      }
    })
    return () => setOnExit(null)
  }, [navigate, setOnExit])

  useEffect(() => {
    setOnMenu(() => setSidebarOpen(true))
    return () => setOnMenu(null)
  }, [setOnMenu])

  const handleCancelExit = useCallback(() => setShowExit(false), [])
  const handleConfirmExit = useCallback(() => {
    setShowExit(false)
    navigate(-1)
  }, [navigate])

  const handleStepSelect = useCallback(
    (step: LearnStep) => {
      if (!loaded) return
      const idx = loaded.sorted.findIndex((c) => c.configComponentId === step.id)
      if (idx >= 0) {
        setActiveIndex(idx)
        setFinished(false)
        finishedRef.current = false
        collapseSidebar()
      }
    },
    [loaded, collapseSidebar],
  )

  const handleComponentComplete = useCallback(async (result?: ComponentCompleteResult) => {
    if (!loaded) return
    const updatedSorted = [...loaded.sorted]
    const currentComp = updatedSorted[activeIndex]
    if (currentComp) {
      // 서버 결과를 반영한다 — 건너뛴 단계를 completed 로 위장하지 않는다(재진입·사이드바 표시 일관).
      const status = result?.status ?? 'completed'
      updatedSorted[activeIndex] = {
        ...currentComp,
        status,
        skipped: status === 'skipped',
        skipReason: status === 'skipped' ? result?.skipReason ?? currentComp.skipReason ?? null : null,
      }
    }
    const { sorted, steps } = toLearnSteps(updatedSorted, i18n.language, labelFallbacks)
    const next = sorted.findIndex(
      (c, i) => i > activeIndex && c.status !== 'completed' && c.status !== 'skipped',
    )
    setLoaded({ ...loaded, sorted, steps })
    if (next < 0) {
      if (mode === 'progress') {
        void queryClient.invalidateQueries({ queryKey: learnerLibraryQueryKey })
      }
      setFinished(true)
      finishedRef.current = true
    } else {
      setActiveIndex(next)
    }
  }, [loaded, activeIndex, i18n.language, labelFallbacks, mode, queryClient])

  if (loading) {
    return (
      <LearnerLoading />
    )
  }

  if (error || !loaded) {
    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <Card
          size="md"
          role="alert"
          aria-live="assertive"
          className="flex flex-col items-center gap-4 py-8 text-center max-w-md"
        >
          <AlertCircle
            aria-label={t('aria.error')}
            className="size-10 text-lc-red-900"
          />
          <p className="text-lc-body-2 text-lc-gray-700">{error ?? t('learn.lessonNotFound')}</p>
          <Button3D variant="lightblue" size="M" onClick={load}>
            {t('common:retry')}
          </Button3D>
        </Card>
      </div>
    )
  }

  if (!activeComp) {
    return finished ? (
      <CompletionCelebration message={loaded.isDesignatedReview ? '' : undefined} onCtaClick={() => navigate('/my/today')} />
    ) : null
  }

  const selectedStepId = activeComp.configComponentId

  return (
    <>
      <div className="hidden md:flex">
        <LearningSidebar
          steps={loaded.steps}
          selectedStepId={selectedStepId}
          onSelect={handleStepSelect}
          variant="inline"
          collapsed={sidebarCollapsed}
          onCollapseToggle={toggleSidebar}
        />
      </div>

      <LearningSidebar
        variant="drawer"
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        steps={loaded.steps}
        selectedStepId={selectedStepId}
        onSelect={(s) => {
          handleStepSelect(s)
          setSidebarOpen(false)
        }}
      />

      <div className="flex-1 flex flex-col min-h-0 min-w-0">
        {sidebarAutoCollapsed && (
          <div
            role="status"
            aria-live="polite"
            className="flex items-center justify-between gap-3 px-4 h-7 bg-lc-lightblue-100 text-lc-lightblue-dark text-lc-caption-1 motion-reduce:animate-none animate-[lc-split-banner_240ms_ease-out]"
          >
            <span className="truncate">{t('split.banner.autoCollapsed')}</span>
            <button
              type="button"
              onClick={expandSidebar}
              className="shrink-0 font-semibold underline hover:no-underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lc-lightblue-900"
            >
              {t('split.banner.expand')}
            </button>
          </div>
        )}
        <ComponentRunner
          key={activeComp.configComponentId}
          runtimeMode={mode === 'review' ? 'library-review' : 'live'}
          headless
          enrollmentId={loaded.enrollmentId}
          lessonId={loaded.lessonId}
          component={activeComp}
          isFirstAttempt={loaded.isFirstAttempt}
          isDesignatedReview={loaded.isDesignatedReview}
          materialNotice={loaded.bookOpenNotice}
          currentIndex={activeIndex}
          totalCount={loaded.sorted.length}
          onComplete={handleComponentComplete}
          onBack={() => setShowExit(true)}
        />
      </div>
      <ExitConfirmDialog open={showExit} onCancel={handleCancelExit} onConfirm={handleConfirmExit} />
      {finished ? (
        <CompletionCelebration message={loaded.isDesignatedReview ? '' : undefined} onCtaClick={() => navigate('/my/today')} />
      ) : null}
    </>
  )
}
